import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { shallowEqual } from 'react-redux';
import Icon, { IconType } from 'components/Icon';
import useScrollMemory from 'hooks/useScrollMemory';
import { DesignSystemSection, DsLink } from 'designSystem/types';
import { DS_TAB_CHROME } from 'designSystem/config';
import { useTheme } from 'styled-components';
import { RootReducer } from 'redux/reducers';
import { selectActiveDesignSystem } from 'redux/db/reducers';
import * as S from './styles';

interface HomeViewProps {
	onNavigate: (section: DesignSystemSection, contentPath?: string, label?: string) => void;
}

const SECTION_DESCRIPTIONS: Partial<Record<DesignSystemSection, string>> = {
	foundations:
		'The underlying building blocks of the system. Choices that everything else is built on.',

	guidelines:
		'Shared principles and guidance that shape how the system is used, extended, and experienced.',

	theme:
		'The raw visual ingredients of the system: colors, type scales, spacing, and other core values.',

	styles:
		'The shared language for expressing visual intent across the system. This is where colors, type, spacing, and surfaces get named for consistent use.',

	components:
		'Reusable interface elements, designed to be consistent, accessible, and ready to use.',

	gallery: 'Real examples that show the system in action across screens and layouts.',

	assets:
		'Brand and visual materials: logos, icon sets, illustrations, and other supporting resources.',
};

const HomeView = ({ onNavigate }: HomeViewProps) => {
	const theme = useTheme();
	const scrollRef = useScrollMemory('home');
	const { designSystemContent, activeDesignSystem } = useSelector(
		(r: RootReducer) => ({
			designSystemContent: r.mode.designSystemContent,
			activeDesignSystem: selectActiveDesignSystem(r.db, r.mode.activeDesignSystemId),
		}),
		shallowEqual
	);

	//----------------------------------------------------------------------
	// DATA
	//----------------------------------------------------------------------
	const guidelines = useMemo(() => {
		return (designSystemContent?.guidelines || []).map((g) => ({
			label: g.title,
			contentPath: g.path,
		}));
	}, [designSystemContent?.guidelines]);

	const components = useMemo(() => {
		return (designSystemContent?.components?.all || []).map((c) => ({
			label: c.name,
			contentPath: c.id,
		}));
	}, [designSystemContent?.components?.all]);

	const gallery = useMemo(() => {
		return (designSystemContent?.gallery || []).map((g) => ({
			label: g.title,
			contentPath: g.path,
		}));
	}, [designSystemContent?.gallery]);

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------
	const renderFeaturedCard = (section: DesignSystemSection) => {
		const chrome = DS_TAB_CHROME[section];
		const description = SECTION_DESCRIPTIONS[section];

		return (
			<S.FeaturedCard onClick={() => onNavigate(section)}>
				<S.FeaturedTitle>
					<Icon type={chrome.icon} width={22} height={22} color={theme.gray[600]} />
					{chrome.label}
				</S.FeaturedTitle>
				{description && <S.FeaturedDescription>{description}</S.FeaturedDescription>}
				<S.CtaBadge>
					View {chrome.label.toLowerCase()}
					<Icon type={IconType.LINE_ARROW_RIGHT} width={12} height={12} />
				</S.CtaBadge>
			</S.FeaturedCard>
		);
	};

	const renderListSection = (
		section: DesignSystemSection,
		links: DsLink[],
		description?: string
	) => {
		if (links.length === 0) return null;
		const chrome = DS_TAB_CHROME[section];

		return (
			<S.SectionCard key={section}>
				<S.SectionTitle>
					<Icon type={chrome.icon} width={20} height={20} color={theme.gray[600]} />
					{chrome.label}
				</S.SectionTitle>
				{description && <S.SectionIntro>{description}</S.SectionIntro>}
				<S.ItemsList>
					{links.map((link) => (
						<S.ItemLink
							key={link.contentPath || link.label}
							onClick={() => onNavigate(section, link.contentPath, link.label)}
						>
							<span>{link.label}</span>
							<S.ArrowIcon>
								<Icon type={IconType.LINE_ARROW_RIGHT} width={12} height={12} />
							</S.ArrowIcon>
						</S.ItemLink>
					))}
				</S.ItemsList>
			</S.SectionCard>
		);
	};

	const renderGridSection = (
		section: DesignSystemSection,
		links: DsLink[],
		allLinkLabel?: string
	) => {
		if (links.length === 0) return null;
		const chrome = DS_TAB_CHROME[section];
		const description = SECTION_DESCRIPTIONS[section];

		return (
			<S.SectionCard>
				<S.SectionTitle>
					<Icon type={chrome.icon} width={20} height={20} color={theme.gray[600]} />
					{chrome.label}
				</S.SectionTitle>
				{description && <S.SectionIntro>{description}</S.SectionIntro>}
				{allLinkLabel && (
					<S.AllItemsLink onClick={() => onNavigate(section)}>
						<span>{allLinkLabel}</span>
						<S.AllLinkArrow>
							<Icon type={IconType.LINE_ARROW_RIGHT} width={12} height={12} />
						</S.AllLinkArrow>
					</S.AllItemsLink>
				)}
				<S.ItemsGrid>
					{links.map((link) => (
						<S.ItemTile
							key={link.contentPath || link.label}
							onClick={() => onNavigate(section, link.contentPath, link.label)}
						>
							{link.label}
						</S.ItemTile>
					))}
				</S.ItemsGrid>
			</S.SectionCard>
		);
	};

	return (
		<S.Container ref={scrollRef}>
			<S.Content>
				<S.Hero>
					<S.HeroContent>
						<S.Title>{activeDesignSystem?.title || 'Design System'}</S.Title>
						<S.Description>
							A comprehensive design system with components, guidelines, and foundations for
							building consistent user experiences.
						</S.Description>
					</S.HeroContent>
				</S.Hero>
				{renderFeaturedCard('foundations')}
				{renderListSection('guidelines', guidelines, SECTION_DESCRIPTIONS.guidelines)}
				{renderListSection(
					'theme',
					[
						{ label: 'All Tokens', contentPath: undefined },
						{ label: 'Colors', contentPath: 'colors' },
					],
					SECTION_DESCRIPTIONS.theme
				)}
				{renderFeaturedCard('styles')}
				{renderGridSection('components', components, 'All Components')}
				{renderGridSection('gallery', gallery, 'Complete Gallery')}
				{renderFeaturedCard('assets')}
			</S.Content>
		</S.Container>
	);
};

export default HomeView;
